/**
 * MediaUploadService
 *
 * Handles media uploads for conversations:
 * - Client-side image compression (max 1200px, 80% quality)
 * - Thumbnail generation
 * - Firebase Storage upload
 * - File size validation (10MB limit)
 *
 * Storage path: conversations/{conversationId}/media/{messageId}/{filename}
 *
 * @module services/messaging/MediaUploadService
 */

import { ref, uploadBytes, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase';
import { isSupportedImageFile, normalizeImageFile } from '../photoStorageService';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const IMAGE_MAX_WIDTH = 1200;
const IMAGE_QUALITY = 0.8;
const THUMBNAIL_MAX_WIDTH = 400;
const THUMBNAIL_QUALITY = 0.85;

class MediaUploadService {
  /**
   * Upload a file for a conversation message
   *
   * @param {string} conversationId
   * @param {File} file - The file to upload
   * @param {Function} onProgress - Optional progress callback (0-100)
   * @returns {Promise<Object>} Media metadata { url, storagePath, type, mimeType, fileName, fileSize, width, height, thumbnailUrl }
   */
  async uploadFile(conversationId, file, onProgress) {
    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      throw new Error(`File too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB`);
    }

    if (isSupportedImageFile(file)) {
      file = await normalizeImageFile(file);
    }

    const isImage = isSupportedImageFile(file);
    const isAudio = file.type.startsWith('audio/');
    const messageId = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
    const basePath = `conversations/${conversationId}/media/${messageId}`;

    let result = {
      storagePath: '',
      url: '',
      type: isImage ? 'image' : isAudio ? 'voice' : 'file',
      mimeType: file.type,
      fileName: file.name,
      fileSize: file.size,
      width: null,
      height: null,
      duration: null,
      thumbnailUrl: null,
      thumbnailStoragePath: null
    };

    if (isImage) {
      // Compress image
      const { blob, width, height } = await this._compressImage(file, IMAGE_MAX_WIDTH, IMAGE_QUALITY);
      result.width = width;
      result.height = height;
      result.fileSize = blob.size;

      // Upload compressed image
      const imagePath = `${basePath}/${file.name}`;
      const imageRef = ref(storage, imagePath);
      await this._uploadWithProgress(imageRef, blob, file.type, onProgress);
      result.url = await getDownloadURL(imageRef);
      result.storagePath = imagePath;

      // Generate and upload thumbnail
      try {
        const { blob: thumbBlob } = await this._compressImage(file, THUMBNAIL_MAX_WIDTH, THUMBNAIL_QUALITY);
        const thumbPath = `${basePath}/thumb_${file.name}`;
        const thumbRef = ref(storage, thumbPath);
        await uploadBytes(thumbRef, thumbBlob, { contentType: file.type });
        result.thumbnailUrl = await getDownloadURL(thumbRef);
        result.thumbnailStoragePath = thumbPath;
      } catch (err) {
        console.warn('Thumbnail generation failed:', err);
      }
    } else {
      // Upload as-is
      const filePath = `${basePath}/${file.name}`;
      const fileRef = ref(storage, filePath);
      await this._uploadWithProgress(fileRef, file, file.type, onProgress);
      result.url = await getDownloadURL(fileRef);
      result.storagePath = filePath;
    }

    return result;
  }

  /**
   * Upload a file to an explicit base path (domain-agnostic).
   *
   * Same pipeline as uploadFile (HEIC→JPEG, compress, thumbnail, progress,
   * 10MB cap) but the caller owns the storage prefix instead of it being
   * conversation-scoped. Used by attachmentUploadService for event attachments.
   * uploadFile is intentionally left untouched so messaging cannot regress.
   *
   * @param {string} basePath - Storage prefix, e.g. schools/{id}/attachments/{domain}/{sessionId}/{attachmentId}
   * @param {File} file
   * @param {Function} [onProgress] - Progress callback (0-100)
   * @returns {Promise<Object>} { url, storagePath, type, mimeType, fileName, fileSize, width, height, thumbnailUrl }
   */
  async uploadToPath(basePath, file, onProgress) {
    if (file.size > MAX_FILE_SIZE) {
      throw new Error(`File too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB`);
    }

    if (isSupportedImageFile(file)) {
      file = await normalizeImageFile(file);
    }

    const isImage = isSupportedImageFile(file);
    const isSvgImage = file.type === 'image/svg+xml' || /\.svg$/i.test(file.name);
    const isAudio = file.type.startsWith('audio/');

    let result = {
      storagePath: '',
      url: '',
      type: isImage ? 'image' : isAudio ? 'voice' : 'file',
      mimeType: file.type,
      fileName: file.name,
      fileSize: file.size,
      width: null,
      height: null,
      duration: null,
      thumbnailUrl: null,
      thumbnailStoragePath: null
    };

    if (isImage && isSvgImage) {
      // SVG is already a compact image format. Passing it through canvas would
      // create raster bytes while retaining the SVG filename/content type,
      // leaving browsers unable to decode the uploaded attachment.
      const imagePath = `${basePath}/${file.name}`;
      const imageRef = ref(storage, imagePath);
      await this._uploadWithProgress(imageRef, file, file.type || 'image/svg+xml', onProgress);
      result.url = await getDownloadURL(imageRef);
      result.storagePath = imagePath;
      result.thumbnailUrl = result.url;
    } else if (isImage) {
      const { blob, width, height } = await this._compressImage(file, IMAGE_MAX_WIDTH, IMAGE_QUALITY);
      result.width = width;
      result.height = height;
      result.fileSize = blob.size;

      const imagePath = `${basePath}/${file.name}`;
      const imageRef = ref(storage, imagePath);
      await this._uploadWithProgress(imageRef, blob, file.type, onProgress);
      result.url = await getDownloadURL(imageRef);
      result.storagePath = imagePath;

      try {
        const { blob: thumbBlob } = await this._compressImage(file, THUMBNAIL_MAX_WIDTH, THUMBNAIL_QUALITY);
        const thumbPath = `${basePath}/thumb_${file.name}`;
        const thumbRef = ref(storage, thumbPath);
        await uploadBytes(thumbRef, thumbBlob, { contentType: file.type });
        result.thumbnailUrl = await getDownloadURL(thumbRef);
        result.thumbnailStoragePath = thumbPath;
      } catch (err) {
        console.warn('Thumbnail generation failed:', err);
      }
    } else {
      const filePath = `${basePath}/${file.name}`;
      const fileRef = ref(storage, filePath);
      await this._uploadWithProgress(fileRef, file, file.type, onProgress);
      result.url = await getDownloadURL(fileRef);
      result.storagePath = filePath;
    }

    return result;
  }

  /**
   * Upload a voice recording blob
   *
   * @param {string} conversationId
   * @param {Blob} audioBlob
   * @param {number} duration - Duration in seconds
   * @returns {Promise<Object>} Media metadata
   */
  async uploadVoiceNote(conversationId, audioBlob, duration) {
    if (audioBlob.size > MAX_FILE_SIZE) {
      throw new Error('Voice note too large');
    }

    const messageId = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
    const fileName = `voice_${messageId}.webm`;
    const filePath = `conversations/${conversationId}/media/${messageId}/${fileName}`;

    const fileRef = ref(storage, filePath);
    await uploadBytes(fileRef, audioBlob, { contentType: 'audio/webm' });
    const url = await getDownloadURL(fileRef);

    return {
      storagePath: filePath,
      url,
      type: 'voice',
      mimeType: 'audio/webm',
      fileName,
      fileSize: audioBlob.size,
      width: null,
      height: null,
      duration: Math.round(duration),
      thumbnailUrl: null
    };
  }

  /**
   * Upload with progress tracking
   * @private
   */
  _uploadWithProgress(storageRef, data, contentType, onProgress) {
    if (!onProgress) {
      return uploadBytes(storageRef, data, { contentType });
    }
    return new Promise((resolve, reject) => {
      const task = uploadBytesResumable(storageRef, data, { contentType });
      task.on('state_changed',
        (snap) => onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
        reject,
        resolve
      );
    });
  }

  /**
   * Compress an image file
   * @private
   */
  _compressImage(file, maxWidth, quality) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);

        let { width, height } = img;

        // Scale down if wider than max
        if (width > maxWidth) {
          height = Math.round((height / width) * maxWidth);
          width = maxWidth;
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve({ blob, width, height });
            } else {
              reject(new Error('Image compression failed'));
            }
          },
          file.type === 'image/png' ? 'image/png' : 'image/jpeg',
          quality
        );
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };

      img.src = url;
    });
  }

  /**
   * Get file type category for display
   */
  getFileType(mimeType) {
    if (mimeType?.startsWith('image/')) return 'image';
    if (mimeType?.startsWith('audio/')) return 'voice';
    if (mimeType?.startsWith('video/')) return 'video';
    if (mimeType === 'application/pdf') return 'pdf';
    if (mimeType?.includes('word') || mimeType?.includes('document')) return 'doc';
    if (mimeType?.includes('sheet') || mimeType?.includes('excel')) return 'spreadsheet';
    if (mimeType?.includes('presentation') || mimeType?.includes('powerpoint')) return 'presentation';
    return 'file';
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes) {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}

export default new MediaUploadService();
